<!-- @format -->
<template>
    <div class="gst-payment d-flex">
        <div class="gst-payment-title d-flex">
            <span>{{ summaryLabel }}</span>
            <span
                v-if="computedShowExpandableDetails"
                tabindex="0"
                @click="showMoreDetails = !showMoreDetails"
                @keypress.enter="showMoreDetails = !showMoreDetails"
                >{{ $t(!showMoreDetails ? 'viewDetails' : 'closeDetails') }}</span
            >
        </div>
        <hr class="gst-hr u-width-100" />
        <v-expand-transition v-if="computedShowExpandableDetails">
            <div
                v-if="showMoreDetails"
                class="gst-payment-details">
                <h5 class="mt-4 mb-2">
                    {{ titlePaymentItems }}
                </h5>
                <div class="detail__item d-flex flex-wrap align-end">
                    <div class="detail__item-row">
                        <div class="detail__item-name flex-grow-1 u-text-transform-by-language">
                            <div
                                v-for="(item, key) in paymentItems"
                                :key="key"
                                class="mb-0">
                                <span class="mr-1">{{
                                    item.name || $t('_common:_terms.room')
                                }}</span>
                                <span>x {{ item.quantity }}</span>
                            </div>
                        </div>
                        <div class="detail__item-price flex-shrink-0">
                            {{ totalPrice | currencyFilter(currency) }}
                        </div>
                    </div>
                    <div
                        v-if="totalIncludedFeeAmount"
                        class="detail__item-row-variant-2 mt-2">
                        <div class="detail__item-name">
                            <span>{{ $t('_common:terms.feesIncluded') }}</span>
                        </div>
                        <div class="detail__item-price flex-shrink-0">
                            {{ totalIncludedFeeAmount | currencyFilter(currency) }}
                        </div>
                    </div>
                </div>
                <div v-if="fees.length">
                    <h5 class="mt-4 mb-2">
                        {{ $t('fees') }}
                    </h5>
                    <div class="detail__item d-flex flex-wrap align-end">
                        <div class="detail__item-row">
                            <div
                                v-for="(item, key) in fees"
                                :key="key"
                                class="d-flex flex-grow-1 align-center justify-between u-width-100">
                                <div
                                    class="detail__item-name flex-grow-1 u-text-transform-by-language">
                                    <div
                                        v-if="
                                            !item.isOrderFee ||
                                            (item.isOrderFee && item.quantity > 1)
                                        ">
                                        <span class="mr-1">{{ item.name }}:</span>
                                        <span class="mr-1">{{
                                            item.price | currencyFilter(currency)
                                        }}</span>
                                        <span>x {{ item.quantity }}</span>
                                    </div>
                                    <div v-else>
                                        {{ item.name }}
                                    </div>
                                </div>
                                <div class="detail__item-price flex-shrink-0">
                                    {{ item.amount | currencyFilter(item.currency) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <hr class="gst-hr" />
            </div>
        </v-expand-transition>
        <div class="gst-payment-method d-flex">
            <div class="gst-payment-info d-flex">
                <span>{{ totalLabel }}</span>
                <span class="gst-payment-info__total"
                    >{{ totalPrice | currencyFilter(currency) }}
                </span>
            </div>
            <div
                v-if="totalPayLaterAmount"
                class="gst-payment-info d-flex pt-4 pb-2">
                <h5 class="d-flex justify-start align-start">
                    {{ $t('_common:terms.payAtProperty') }}
                    <BaseTooltip
                        top
                        :text="feesHotelsNotIncludedEnum"
                        class-content="gst-payment-info__tooltip-content"
                        class="gst-payment-info__tooltip"
                        class-activator="gst-payment-info__tooltip-activator">
                        <BaseIcon
                            symbol-id="icons--help"
                            :width="18"
                            :height="18" />
                    </BaseTooltip>
                </h5>
                <div class="detail__item-price">
                    {{ totalPayLaterAmount | currencyFilter(currency) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import StatusMixin from '@tenant/app/modules/hotel/features/manageBooking/mixins/StatusMixin.js';

export default {
    name: 'PaymentSummary',
    mixins: [StatusMixin],
    props: {
        currency: {
            type: String,
            required: true,
        },
        paymentItems: {
            type: Array,
            required: true,
        },
        hasHotelReservationOnly: {
            type: Boolean,
            required: true,
        },
        totalIncludedFeeAmount: {
            type: [Number, null],
            default: null,
        },
        totalPayLaterAmount: {
            type: [Number, null],
            default: null,
        },
        totalPrice: {
            type: [Number, null],
            default: null,
        },
        totalBasePrice: {
            type: [Number, null],
            default: null,
        },
        fees: {
            type: [Array, null],
            default: null,
        },
        feesHotelsNotIncludedEnum: {
            type: [String, null],
            default: null,
        },
        showExpandableDetails: {
            type: Boolean,
            default: true,
        },
    },
    data: function () {
        return {
            showMoreDetails: false,
        };
    },
    // TODOMMH: Move locales to order module
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.cart.theOrderConfirmation',
    },
    computed: {
        titlePaymentItems: function () {
            if (this.hasHotelReservationOnly) {
                return this.$t('_common:terms.hotelAddOn');
            }
            return this.$t('_common:terms.bundleTicketsHotel');
        },
        summaryLabel() {
            return !this.isStatusRefunded
                ? this.$t('paymentSummary')
                : this.$t(
                      'main:modules.hotel.features.manageBooking.theManageBooking.bookingContent.refunded._components.paymentSummary.refundSummary'
                  );
        },
        totalLabel() {
            return !this.isStatusRefunded
                ? this.$t('main:modules.order.components.paymentSummary.totalPaid')
                : this.$t(
                      'main:modules.hotel.features.manageBooking.theManageBooking.bookingContent.refunded._components.paymentSummary.totalRefund'
                  );
        },
        computedShowExpandableDetails() {
            return (
                this.showExpandableDetails ||
                !(this.isStatusRefunded || this.isStatusFailed || this.isPastBooking)
            );
        },
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

.gst-payment {
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;

    .gst-payment-info {
        line-height: line-height('xxxl');
        font-size: font-size('l');
        font-weight: font-weight('bold');
        align-items: center;
        justify-content: space-between;

        .gst-payment-info__tooltip-activator {
            margin-top: 2px;
            margin-left: theme-spacing(2);
        }
    }

    .gst-payment-title {
        width: 100%;
        line-height: line-height('xxl');
        padding-bottom: theme-spacing(4);
        font-size: font-size('m');
        font-weight: font-weight('bold');
        align-items: center;
        justify-content: space-between;

        span:nth-child(2) {
            color: theme-color('primary') !important;
            cursor: pointer;
        }
    }

    .gst-payment-method {
        width: 100%;
        margin-top: theme-spacing(5);
        flex-direction: column;

        .gst-payment-info {
            line-height: line-height('xxxl');
            font-size: font-size('l');
            font-weight: font-weight('bold');
            align-items: center;
            justify-content: space-between;

            .detail__item-price {
                font-size: font-size('xxs');
                font-weight: font-weight('regular');
            }
        }
    }

    .gst-payment-details {
        width: 100%;

        h5 {
            line-height: line-height('xxxl');
            color: theme-color('quaternary');
            font-size: font-size('l');
            font-weight: font-weight('large');
        }

        .detail__item {
            padding-bottom: theme-spacing(6);
        }

        .detail__item-name,
        .detail__item-price {
            line-height: line-height('xl');
            color: theme-color('quaternary');
            font-size: font-size('s');
            font-weight: font-weight('regular');
        }

        .detail__item-row {
            display: flex;
            width: 100%;
            flex-wrap: wrap;
        }

        .detail__item-row-variant-2 {
            display: flex;
            flex-grow: 1;

            .detail__item-name {
                display: flex;
                line-height: line-height('m');
                padding-right: theme-spacing(2);
                color: theme-color('tertiary');
                font-size: font-size('xxs');
                flex-direction: row;
                flex-grow: 1;

                span {
                    margin-right: theme-spacing(4);
                }

                svg {
                    height: 18px;
                    width: 18px;

                    .gst-svg-icon {
                        stroke: theme-color('tertiary') !important;
                    }

                    .gst-svg-icon:nth-child(2) {
                        fill: transparent !important;
                    }
                }
            }

            .detail__item-price {
                line-height: line-height('m');
                color: theme-color('tertiary');
                font-size: font-size('xxs');
            }
        }
    }
}

.gst-payment__tooltip-content.v-tooltip__content,
.gst-payment-info__tooltip-content.v-tooltip__content {
    max-width: 316px !important;
}
</style>
